import type { RichData } from '../types';

// ── XSS escape ──
export function esc(str: unknown): string {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ── Toast ──
const TOAST_ICONS: Record<string, string> = {
  success: 'ti-circle-check',
  error:   'ti-circle-x',
  warning: 'ti-alert-triangle',
  info:    'ti-info-circle',
};
const TOAST_BG: Record<string, string> = {
  success: '#16A34A',
  error:   '#DC2626',
  warning: '#EA580C',
  info:    '#111827',
};

export function toast(type: string, msg: string): void {
  const el = document.getElementById('toast')!;
  el.innerHTML = `<i class="ti ${TOAST_ICONS[type] ?? 'ti-info-circle'}"></i>`;
  el.appendChild(document.createTextNode(msg));
  el.style.background = TOAST_BG[type] ?? '#111827';
  el.classList.add('show');
  setTimeout(() => el.classList.remove('show'), 3000);
}

// ── Modal ──
export function closeModal(id: string): void {
  document.getElementById(id)!.classList.remove('open');
}

// ── Utils ──
export function parseEmp(raw: string): { empId: string; empName: string; branch: string } {
  const sep = (raw ?? '').includes('/') ? '/' : '|';
  const parts = (raw ?? '').split(sep).map(s => s.trim());
  if (parts.length >= 3) return { empId: parts[0] || '—', empName: parts[1] || '—', branch: parts[2] || '—' };
  return { empId: '—', empName: raw ?? '—', branch: '—' };
}

export function pillClass(s: string): string {
  return ({ new: 'p-new', processing: 'p-proc', done: 'p-done' } as Record<string, string>)[s] ?? 'p-new';
}
export function pillIcon(s: string): string {
  return ({ new: 'ti-circle-dot', processing: 'ti-loader', done: 'ti-circle-check' } as Record<string, string>)[s] ?? 'ti-circle-dot';
}
export function pillLabel(s: string): string {
  return ({ new: 'ใหม่', processing: 'กำลังดำเนินการ', done: 'เสร็จแล้ว' } as Record<string, string>)[s] ?? s;
}
export function topicIcon(t: string): string {
  const map: Record<string, string> = {
    'ลางาน': 'ti-calendar-off',
    'เบิกค่าใช้จ่าย': 'ti-receipt',
    'ขอเอกสาร': 'ti-file-text',
    'ปัญหาเงินเดือน': 'ti-currency-baht',
    'อื่นๆ': 'ti-message-dots',
  };
  return map[t] ?? 'ti-ticket';
}

// ── Rich Editor ──
export function buildRichEditor(text = '', imageUrl = '', links: Array<{ label: string; url: string }> = [], placeholder = ''): string {
  const hasImg = !!imageUrl;
  const safeText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const linksHtml = links.map(l => `
    <div class="rich-link-row">
      <input class="link-label" placeholder="ชื่อปุ่ม" value="${esc(l.label ?? '')}">
      <input class="link-url" type="url" placeholder="https://..." value="${esc(l.url ?? '')}">
      <button type="button" class="icon-btn danger" onclick="this.closest('.rich-link-row').remove()"><i class="ti ti-minus"></i></button>
    </div>`).join('');
  return `<div class="rich-editor">
    <div class="rich-toolbar">
      <button type="button" class="rich-btn" onclick="richFormat(this,'bold')" title="ตัวหนา"><b>B</b></button>
      <button type="button" class="rich-btn" onclick="richFormat(this,'italic')" title="ตัวเอียง"><i style="font-style:italic">I</i></button>
      <button type="button" class="rich-btn" onclick="richFormat(this,'underline')" title="ขีดเส้นใต้"><u>U</u></button>
      <div class="rich-sep"></div>
      <button type="button" class="rich-btn ${hasImg ? 'active' : ''}" onclick="richToggleImg(this)" title="แนบรูปภาพ"><i class="ti ti-photo"></i> รูป</button>
      <button type="button" class="rich-btn" onclick="richAddLink(this)" title="เพิ่มปุ่มลิงก์"><i class="ti ti-link"></i> ลิงก์</button>
    </div>
    <textarea class="rich-text" placeholder="${esc(placeholder)}" rows="3">${safeText}</textarea>
    <div class="rich-extra">
      <div class="rich-img-row" style="${hasImg ? '' : 'display:none'}">
        <div style="font-size:10px;color:var(--muted);margin-bottom:3px"><i class="ti ti-photo"></i> URL รูปภาพ</div>
        <input class="rich-img-inp" type="url" placeholder="https://example.com/image.jpg" value="${esc(imageUrl)}">
      </div>
      <div class="rich-links-list">${linksHtml}</div>
    </div>
  </div>`;
}

export function richFormat(btn: HTMLElement, type: string): void {
  const ta = btn.closest('.rich-editor')!.querySelector('.rich-text') as HTMLTextAreaElement;
  const s = ta.selectionStart, e = ta.selectionEnd;
  const sel = ta.value.slice(s, e) || 'ข้อความ';
  const markers: Record<string, [string, string]> = { bold: ['**', '**'], italic: ['_', '_'], underline: ['__', '__'] };
  const [o, c] = markers[type] ?? ['', ''];
  ta.value = ta.value.slice(0, s) + o + sel + c + ta.value.slice(e);
  ta.focus();
  ta.setSelectionRange(s + o.length, s + o.length + sel.length);
}

export function richToggleImg(btn: HTMLElement): void {
  const row = btn.closest('.rich-editor')!.querySelector('.rich-img-row') as HTMLElement;
  const show = row.style.display === 'none';
  row.style.display = show ? '' : 'none';
  btn.classList.toggle('active', show);
  if (show) (row.querySelector('input') as HTMLInputElement).focus();
}

export function richAddLink(btn: HTMLElement): void {
  const list = btn.closest('.rich-editor')!.querySelector('.rich-links-list')!;
  const row = document.createElement('div');
  row.className = 'rich-link-row';
  row.innerHTML = `<input class="link-label" placeholder="ชื่อปุ่ม"><input class="link-url" type="url" placeholder="https://..."><button type="button" class="icon-btn danger" onclick="this.closest('.rich-link-row').remove()"><i class="ti ti-minus"></i></button>`;
  list.appendChild(row);
  (row.querySelector('input') as HTMLInputElement).focus();
}

export function getRichData(container: HTMLElement): RichData {
  const ed = container.querySelector?.('.rich-editor') ?? container.closest?.('.rich-editor');
  if (!ed) return { text: '', imageUrl: '', links: [] };
  const text = (ed.querySelector('.rich-text') as HTMLTextAreaElement).value.trim();
  const imgInp = ed.querySelector('.rich-img-inp') as HTMLInputElement | null;
  const imageUrl = imgInp?.value.trim() ?? '';
  const links = [...ed.querySelectorAll('.rich-link-row')].map(r => ({
    label: (r.querySelector('.link-label') as HTMLInputElement).value.trim(),
    url:   (r.querySelector('.link-url')   as HTMLInputElement).value.trim(),
  })).filter(l => l.label && l.url);
  return { text, imageUrl, links };
}

// ── Custom Dropdown ──
export function toggleCdd(id: string): void {
  const isOpen = document.getElementById(`${id}Menu`)!.classList.contains('open');
  document.querySelectorAll('.cdd-menu.open').forEach(m => m.classList.remove('open'));
  document.querySelectorAll('.cdd-btn.open').forEach(b => b.classList.remove('open'));
  if (!isOpen) {
    document.getElementById(`${id}Menu`)!.classList.add('open');
    document.getElementById(id)!.querySelector('.cdd-btn')!.classList.add('open');
  }
}
export function closeCdd(id: string): void {
  document.getElementById(`${id}Menu`)!.classList.remove('open');
  document.getElementById(id)!.querySelector('.cdd-btn')!.classList.remove('open');
}

export function initDropdownDismiss(): void {
  document.addEventListener('click', e => {
    if (!(e.target as HTMLElement).closest('.cdd')) {
      document.querySelectorAll('.cdd-menu.open').forEach(m => m.classList.remove('open'));
      document.querySelectorAll('.cdd-btn.open').forEach(b => b.classList.remove('open'));
    }
  });
}
